import PermissionDenied from "@/components/common/PermissionDenied";
import { useAuthStore } from "@/store/auth/authUserStore";
import { lazyImport } from "@/utils/lazyImport";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

const { ListRoutes } = lazyImport(
  () => import("@/features/list"),
  "ListRoutes"
);
const { WorkflowsRoutes } = lazyImport(
  () => import("@/features/workflow"),
  "WorkflowsRoutes"
);
const { DataChecksRoutes } = lazyImport(
  () => import("@/features/dataCheck"),
  "DataChecksRoutes"
);
const { RegisterRoutes } = lazyImport(
  () => import("@/features/register"),
  "RegisterRoutes"
);
const { UserRoutes } = lazyImport(
  () => import("@/features/user"),
  "UserRoutes"
);

const { ProcessSettingRoutes } = lazyImport(
  () => import("@/features/process/documentSetting"),
  "ProcessSettingRoutes"
);

const { SortResultEditRoutes } = lazyImport(
  () => import("@/features/process/sortResultEdit"),
  "SortResultEditRoutes"
);

const { PasswordSettingRoutes } = lazyImport(
  () => import("@/features/passwordSetting"),
  "PasswordSettingRoutes"
);

const { IPAddressSettingRoutes } = lazyImport(
  () => import("@/features/ipAddressSetting"),
  "IPAddressSettingRoutes"
);

const { NewPasswordSettingRoutes } = lazyImport(
  () => import("@/features/newPasswordSetting"),
  "NewPasswordSettingRoutes"
);

const { DictionarySettingPage } = lazyImport(
  () => import("@/pages/dictionarySetting/route"),
  "DictionarySettingPage"
);
const { DictionaryItemSettingPage } = lazyImport(
  () => import("@/pages/dictionarySetting/[dictionary_id]/route"),
  "DictionaryItemSettingPage"
);

interface ProtectedRouteProps {
  element: ReactNode;
  roles?: string[];
}

const ProtectedRoute = ({ element, roles }: ProtectedRouteProps) => {
  const { user } = useAuthStore();

  if (user && !roles?.includes(user.role!)) {
    return <PermissionDenied />;
  }

  return <>{element}</>;
};

export const protectedRoutes = [
  {
    // NOTE: "/"にアクセスした場合は"/views"にリダイレクトする
    path: "/",
    element: <Navigate to="/views" replace />,
  },
  {
    path: "/views/*",
    element: <ListRoutes />,
  },
  {
    path: "/workflow/:workflow_id/dataCheck/*",
    element: <DataChecksRoutes />,
  },
  {
    path: "/workflow/*",
    element: <WorkflowsRoutes />,
  },
  {
    path: "/workflow/:workflow_id/register/*",
    element: <RegisterRoutes />,
  },
  {
    path: "/users/*",
    element: (
      <ProtectedRoute
        element={<UserRoutes />}
        roles={["admin", "superadmin"]}
      />
    ),
  },
  {
    path: "/dictionarySetting/*",
    element: (
      <Routes>
        <Route path="" element={<DictionarySettingPage />} />
        <Route path="/:dictionary_id" element={<DictionaryItemSettingPage />} />
      </Routes>
    ),
  },
  {
    path: "/:workflow_id/process/documentSetting",
    element: <ProcessSettingRoutes />,
  },
  {
    path: "/:workflow_id/process/sortResultEdit",
    element: <SortResultEditRoutes />,
  },
  {
    path: "/passwordSetting/*",
    element: (
      <ProtectedRoute
        element={<PasswordSettingRoutes />}
        roles={["admin", "superadmin"]}
      />
    ),
  },
  {
    path: "/IPAddressSetting/*",
    element: (
      <ProtectedRoute
        element={<IPAddressSettingRoutes />}
        roles={["admin", "superadmin"]}
      />
    ),
  },
  {
    path: "/newPasswordSetting/*",
    element: <NewPasswordSettingRoutes />,
  },
];
